import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Helmet } from "react-helmet-async";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  formation: "",
  description: "",
  statut: "En attente",
  id_user: "1",
};

const FormationSurMesure = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialState);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { nom, prenom, email, telephone, formation, description } = formData;

    if (!nom || !prenom || !email || !telephone || !formation || !description) {
      setAlert({
        type: "error",
        message: "Veuillez remplir tous les champs obligatoires.",
      });
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      // Use axios directly for CancelToken
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000
      );

      const response = await apiClient.post(`/demandes_formation`, formData, {
        headers: { "Content-Type": "application/json" },
        cancelToken: source.token,
      });

      clearTimeout(timeoutId);

      if (response.status === 200 || response.status === 201) {
        setAlert({
          type: "success",
          message: "Votre demande a été envoyée avec succès !",
        });
        setFormData(initialState);
        setTimeout(() => navigate("/"), 2000);
      } else {
        throw new Error(
          response.data.message || "Erreur lors de l'envoi de la demande"
        );
      }
    } catch (err) {
      let errorMessage = "Une erreur est survenue. Veuillez réessayer.";
      // Use axios.isCancel directly
      if (axios.isCancel(err)) {
        errorMessage =
          "Délai d'attente dépassé : Veuillez vérifier votre connexion et réessayer.";
      } else if (err.response) {
        errorMessage = `Erreur du serveur : ${err.response.status} - ${
          err.response.data?.message || "Erreur lors de l'envoi de la demande"
        }`;
      } else if (err.request) {
        errorMessage =
          "Erreur réseau : Impossible de contacter le serveur. Veuillez vérifier votre connexion.";
      } else {
        errorMessage = `Erreur : ${err.message}`;
      }
      setSubmitError(errorMessage);
      setAlert({ type: "error", message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    setSubmitError(null);
    handleSubmit(new Event("submit"));
  };

  return (
    <>
      <Helmet>
        <title>Formation Sur Mesure – DVELGE Formation</title>
        <meta
          name="description"
          content="Demandez une formation personnalisée adaptée à vos objectifs. DVELGE Formation propose des programmes sur mesure à Conakry en développement, cybersécurité, data et IT."
        />
        <meta
          name="keywords"
          content="formation sur mesure Conakry, formation personnalisée Guinée, centre informatique Conakry, formation IT entreprise Guinée, DVELGE Formation"
        />
        <meta
          property="og:title"
          content="Formation Sur Mesure – DVELGE Formation"
        />
        <meta
          property="og:description"
          content="Programmes de formation personnalisés pour particuliers et entreprises à Conakry."
        />
        <meta
          property="og:image"
          content="https://formation.dvelgegroup.com/assets/img/og-image-1200x630.jpg"
        />
        <meta
          property="og:url"
          content="https://formation.dvelgegroup.com/formation-sur-mesure"
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Formation Sur Mesure",
            description:
              "Service de création de programmes de formation personnalisés adaptés aux besoins des individus et des entreprises.",
            provider: {
              "@type": "EducationalOrganization",
              name: "DVELGE Formation",
              url: "https://formation.dvelgegroup.com",
            },
            areaServed: "Conakry, Guinée",
            serviceType: "Formation informatique personnalisée",
          })}
        </script>
      </Helmet>

      <main className="main-area fix">
        <section className="breadcrumb__area breadcrumb__bg">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumb__content">
                  <h3 className="title">Formation Sur Mesure</h3>
                  <nav className="breadcrumb">
                    <span>
                      <Link to="/">Accueil</Link>
                    </span>
                    <span className="breadcrumb-separator">
                      <i className="fas fa-angle-right"></i>
                    </span>
                    <span>Formation Sur Mesure</span>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="singUp-area section-py-120">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-6 col-lg-8">
                <div className="singUp-wrap">
                  <h4 className="title">
                    {" "}
                    DEMANDER UNE FORMATION PERSONNALISÉE{" "}
                  </h4>
                  <p>Remplissez ce formulaire pour soumettre votre demande.</p>

                  {submitError && (
                    <div className="alert alert-warning mb-4" role="alert">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>
                          <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                          {submitError}
                        </small>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleRetry}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>{" "}
                              Envoi...
                            </>
                          ) : (
                            "Réessayer"
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {alert.message && !submitError && (
                    <div
                      className={`alert ${
                        alert.type === "success"
                          ? "alert-success"
                          : "alert-danger"
                      }`}
                    >
                      {alert.message}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="account__form">
                    <div className="row gutter-20">
                      <div className="col-md-6">
                        <div className="form-grp">
                          <label htmlFor="nom">Nom</label>
                          <input
                            id="nom"
                            name="nom"
                            type="text"
                            placeholder="Votre nom"
                            value={formData.nom}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-grp">
                          <label htmlFor="prenom">Prénom</label>
                          <input
                            id="prenom"
                            name="prenom"
                            type="text"
                            placeholder="Votre prénom"
                            value={formData.prenom}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-grp">
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Votre email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-grp">
                      <label htmlFor="telephone">Téléphone</label>
                      <input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        placeholder="Votre téléphone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-grp">
                      <label htmlFor="formation">Formation souhaitée</label>
                      <input
                        id="formation"
                        name="formation"
                        type="text"
                        placeholder="Nom de la formation"
                        value={formData.formation}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-grp">
                      <label htmlFor="description">
                        {" "}
                        Description de votre projet{" "}
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows="4"
                        placeholder="Décrivez votre projet et vos objectifs"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="form-grp text-center">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={
                          !formData.nom ||
                          !formData.prenom ||
                          !formData.email ||
                          !formData.telephone ||
                          !formData.formation ||
                          !formData.description ||
                          loading
                        }
                      >
                        {submitError ? (
                          <>
                            <i className="fas fa-redo me-2"></i> Réessayer
                            l'envoi
                          </>
                        ) : (
                          "Envoyer la demande"
                        )}
                      </button>
                    </div>

                    {retryCount >= 2 && submitError && (
                      <div className="alert alert-info mt-4 text-center">
                        <h6>Problème persistant ?</h6>
                        <p className="mb-2">
                          Vous pouvez également nous contacter directement :
                        </p>
                        <div className="d-flex justify-content-center gap-3">
                          <a
                            href="tel:+224627020871"
                            className="btn btn-outline-primary btn-sm"
                          >
                            <i className="fas fa-phone me-1"></i> Appeler
                          </a>
                          <a
                            href="mailto:formation@dvelgegroup.com"
                            className="btn btn-outline-primary btn-sm"
                          >
                            <i className="fas fa-envelope me-1"></i> Email
                          </a>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default FormationSurMesure;
